import { ENV } from 'shared/ENV';
import { InAppReview } from '@capacitor-community/in-app-review';
import * as Sentry from '@sentry/capacitor';
import { Capacitor } from '@capacitor/core';
import { Filesystem } from '@capacitor/filesystem';
import { Photo } from '@watchduty/camera';
import axios, { CancelTokenSource } from 'axios';
import levenshtein from 'js-levenshtein';
import { generateJSON, generateText } from '@tiptap/react';
import { RICH_TEXT_EDITOR_EXTENSIONS } from 'components/RichTextEditor/extensions';
import * as Yup from 'yup';
import { RequiredStringSchema } from 'yup/lib/string';
import {
  AndroidSettings,
  IOSSettings,
  NativeSettings,
} from 'capacitor-native-settings';
import { MapSearchResult } from 'hooks/useMapSearch/useMapSearch.types';
import { Wildfire } from 'features/wildfires/wildfire.types';
import { isEmpty, isEqual, transform } from 'es-toolkit/compat';
import { capitalize } from 'es-toolkit';
import {
  NotificationRegion,
  NotificationSetting,
  RegionSection,
} from 'features/notifications/notifications.types';
import {
  NotificationSettingTypes,
  NotificationTypes,
} from 'features/notifications/notifications.constants';
import { TFunction } from 'i18next';
import { GeoEventRegion } from 'features/geoEvents/geoEventRegion.types';
import { Flooding } from 'features/flooding/flooding.types';
import { CriticalAlerts } from 'libs/cap-plugins/critical-alerts';
import {
  FormEvacZoneStatus,
  GeoEventEvacZoneStatus,
  GPSData,
  MapLocation,
  RegionEvacZoneStyle,
  UploadImageData,
  User,
  UserGroups,
} from './types';
import {
  COORDINATE_DISPLAY_FRACTION_DIGITS,
  EMPTY_DATA_VALUE,
  EvacZoneStyles,
  PhoneNumberRegex,
  PhoneSMSRegex,
  UsStatesAbbr,
} from '../constants';
import { formatLocalDateToUtc } from './dates';
import { CoordType } from './yup-extended';

// Returns an array of [acreage number as string, singular/plural form of "acre"]
export const getAcreageStrParts = (
  acreage: number | null,
  containment: number | null,
  locale?: string,
): Array<string | null> => {
  // This conditional matches the logic used in src/components/IncidentCard to decide
  // when to display the incident/containment boxes.
  if (!acreage && !containment) return [null, null];
  if (acreage === null) return [null, null];
  if (acreage === 0) return ['< 1', 'acre'];
  if (acreage === 1) return ['1', 'acre'];
  const formattedAcreage = locale
    ? Intl.NumberFormat(locale).format(acreage)
    : acreage.toLocaleString();
  return [formattedAcreage, 'acres'];
};

/**
 * This function groups notification settings counties by state, using
 * an object which keys are the notification region settings "stateDisplayName"
 * (e.g. "California"), and also checks if all counties in a state
 * are not Reporter Covered in which case the county's region "isReporterCoveredForFire"
 * property is false.
 */
export const getRegionSettingsSections = (
  regionSettings: NotificationSetting[],
  searchQuery: string | null,
): Record<string, RegionSection> => {
  const sections: Record<string, RegionSection> = {};
  const searchQueryLower = searchQuery?.toLowerCase();

  regionSettings.forEach((regionSetting) => {
    // only include regions if they match the search query (if search query exists)
    if (searchQueryLower) {
      const matchesSearch = regionSetting.region.displayName
        .toLowerCase()
        .includes(searchQueryLower);
      if (!matchesSearch) return null;
    }

    if (!sections[regionSetting.region.stateDisplayName]) {
      sections[regionSetting.region.stateDisplayName] = {
        title: regionSetting.region.stateDisplayName,
        regionSettings: [],
        isReporterCoveredForFire: false,
      };
    }

    sections[regionSetting.region.stateDisplayName].regionSettings.push(
      regionSetting,
    );

    if (
      !sections[regionSetting.region.stateDisplayName]
        .isReporterCoveredForFire &&
      regionSetting.region.isReporterCoveredForFire
    ) {
      sections[regionSetting.region.stateDisplayName].isReporterCoveredForFire =
        true;
    }

    return sections;
  });

  return sections;
};

export const getSortedRegionsSections = (
  regionSettings: NotificationSetting[],
  searchQuery: string | null,
): RegionSection[] => {
  const regionSections = getRegionSettingsSections(regionSettings, searchQuery);

  return Object.keys(regionSections)
    .sort()
    .map((title) => regionSections[title]);
};

export const getActiveSettings = (
  regionSettings: NotificationSetting[],
): NotificationSetting[] =>
  regionSettings.filter(
    (regionSetting) => regionSetting.setting !== NotificationTypes.OFF.key,
  );

/**
 *
 * @param timeMs delay in miliseconds
 */
export const waitFor = (timeMs: number): Promise<void> =>
  new Promise<void>((resolve) => {
    setTimeout(() => resolve(), timeMs);
  });

export const userInGroups = (
  user: User | null,
  groupNames: UserGroups[],
): boolean => {
  if (!user) {
    return false;
  }
  // legacy local storage user does not have user.groups
  if (!user.groups) {
    return false;
  }
  return user.groups.some((group) => groupNames.includes(group.name));
};

export const getRandomId = (): number => {
  return Math.floor(Math.random() * Date.now());
};

// Stolen from here - https://stackoverflow.com/a/30773300
const metersPerPixel = (latitude: number, zoomLevel: number): number => {
  const earthRadius = 6378137;
  const tileSize = 512;
  const earthCircumference = 2 * Math.PI * earthRadius;
  const scale = 2 ** zoomLevel;
  const worldSize = tileSize * scale;
  const latitudeRadians = latitude * (Math.PI / 180);
  return (earthCircumference * Math.cos(latitudeRadians)) / worldSize;
};

export const metersToPixels = (
  latitude: number,
  meters: number,
  zoomLevel: number,
): number => {
  return meters / metersPerPixel(latitude, zoomLevel);
};

/** @deprecated by getMapSearchResultName() in useMapSearch() */
export const getMapSearchResultName = (location: MapSearchResult): string => {
  let name = '';
  if (location.address) name += `${location.address} `;
  name += location.text;
  return name;
};

export const removeAddressCountry = (address: string): string => {
  return address.replace(/(,\s*)?(united states|usa)$/i, '');
};

export const abbrState = (address: string): string => {
  return address.replace(/\b([A-Z][a-z]+(?: [A-Z][a-z]+)?)\b/g, (match) => {
    // @ts-expect-error it's OK if there's no... match
    return UsStatesAbbr[match] || match;
  });
};

export const getMapSearchResultFormattedAddress = (
  name: string,
  placeName: string,
): string => {
  return abbrState(
    removeAddressCountry(
      placeName.replace(name, '').split(',').filter(Boolean).join(','),
    ),
  );
};

export const mapSearchResultToMapLocation = (
  result: MapSearchResult,
): MapLocation => {
  const name = getMapSearchResultName(result);
  return {
    name,
    lat: result.center[1],
    lng: result.center[0],
    bbox: result.bbox,
    place: false,
    formattedAddress:
      result.placeAddress ||
      getMapSearchResultFormattedAddress(name, result.placeName),
    type: result.type,
    geoEventId: parseInt(result.id, 10),
  };
};

export const knotsToMph = (knots: number): number => {
  return Math.round(knots * 1.15078);
};

export const replaceAtIndex = <T>(array: T[], index: number, item: T): T[] => {
  const cloneArray = [...array];

  cloneArray.splice(index, 1, item);

  return cloneArray;
};

export const formatToUSDollars = (amount: number, digits = 0): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: digits,
  }).format(amount);
};

export const formatNumber = (
  number: number,
  fractionDigits?: number,
): string => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(number);
};

export const escapeRegExp = (string: string): string => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
};

export const getValidRegExp = (term: string, flags?: string): RegExp | null => {
  try {
    const regExp = new RegExp(term, flags);
    return regExp;
  } catch (error) {
    return null;
  }
};

export const isValidLat = (value: number): boolean => {
  return value >= -90 && value <= 90;
};

export const isValidLng = (value: number): boolean => {
  return value >= -180 && value <= 180;
};

/**
 * Truncates a string to a given length ensuring the result always
 * ends at the end of a complete word. It avoids breaking words,
 * which may lead to the returned result being slightly longer than
 * the designated maximum length.
 * @param str - string to truncate
 * @param limit - maximum length to truncate
 * @returns truncated string
 */
export const removeNewLines = (str: string): string => {
  // Removing line breaks from str
  return str.replace(/(\r\n|\n|\r)/gm, ' ');
};

export const truncateString = (str: string, limit: number): string => {
  if (str.length <= limit) return str;

  // Minus ellipsis characters length
  const spaceAfterLimitIndex = str.indexOf(' ', limit - 3);

  if (spaceAfterLimitIndex === -1) return str;

  return `${str.slice(0, spaceAfterLimitIndex).replace(/\.$/g, '')}...`;
};

export const parseLatitudeValue = (
  latitude: string | number | null | undefined,
): number | undefined => {
  if (latitude == null || latitude === '') return undefined;

  const lat = Number(latitude);

  if (Number.isNaN(lat) || !isValidLat(lat)) return undefined;

  return lat;
};

export const parseLongitudeValue = (
  longitude: string | number | null | undefined,
): number | undefined => {
  if (longitude == null || longitude === '') return undefined;

  const lng = Number(longitude);

  if (Number.isNaN(lng) || !isValidLng(lng)) return undefined;

  return lng;
};

export const parseZoomValue = (
  zoom: string | number | null | undefined,
): number | undefined => {
  if (zoom == null || zoom === '') return undefined;

  const zoomLevel = Number(zoom);

  if (Number.isNaN(zoomLevel)) return undefined;

  return zoomLevel;
};

export const getRegionName = (regions: GeoEventRegion[]): string => {
  const region = regions[0];
  if (!region) return '';
  let name = `${region.displayName}, ${region.state}`;
  if (regions.length > 1) {
    name += ' & ...';
  }
  return name;
};

export const parseCoordinate = (number: number): number =>
  parseFloat(number.toFixed(COORDINATE_DISPLAY_FRACTION_DIGITS));

const convertDMSToDD = (
  degrees: number,
  minutes: number,
  seconds: number,
  direction?: string,
): number => {
  let dd = degrees + minutes / 60 + seconds / (60 * 60);

  // Direction is case insensitive
  if (direction && /^[SW]$/i.test(direction)) {
    dd *= -1;
  }
  // Don't do anything for N or E

  return dd;
};

// https://stackoverflow.com/questions/1140189/converting-latitude-and-longitude-to-decimal-values
export const parseDMS = (
  input: string,
): { latitude: number; longitude: number } => {
  const parts = input.split(/[^\d\w-.]+/).filter(Boolean);
  let lat = 0;
  let lng = 0;
  if (parts.length === 8) {
    lat = convertDMSToDD(
      Number(parts[0]),
      Number(parts[1]),
      Number(parts[2]),
      parts[3],
    );
    lng = convertDMSToDD(
      Number(parts[4]),
      Number(parts[5]),
      Number(parts[6]),
      parts[7],
    );
  } else if (parts.length === 4) {
    const latDegrees = Number(parts[0]);
    const [ltMins, ltSecsDec] = parts[1].split('.');
    const latMins = Number(ltMins);
    const latSecs = ltSecsDec ? Number(`0.${ltSecsDec}`) * 60 : 0; // To find the seconds, multiply the decimal part by 60

    const lngDegrees = Number(parts[2]);
    const [lnMins, lnSecsDec] = parts[3].split('.');
    const lngMins = Number(lnMins);
    const lngSecs = lnSecsDec ? Number(`0.${lnSecsDec}`) * 60 : 0; // To find the seconds, multiply the decimal part by 60

    lat = convertDMSToDD(
      Math.abs(latDegrees),
      latMins,
      latSecs,
      latDegrees < 0 ? 'S' : 'N',
    );
    lng = convertDMSToDD(
      Math.abs(lngDegrees),
      lngMins,
      lngSecs,
      lngDegrees < 0 ? 'W' : 'E',
    );
  } else if (parts.length === 6) {
    lat = convertDMSToDD(
      Number(parts[0]),
      Number(parts[1]),
      0, // No seconds
      parts[2],
    );
    lng = convertDMSToDD(
      Number(parts[3]),
      Number(parts[4]),
      0, // No seconds
      parts[5],
    );
  }
  return {
    latitude: parseCoordinate(lat),
    longitude: parseCoordinate(lng),
  };
};

export const getLatLngLocationName = (lat: number, lng: number): string => {
  return `${lat.toFixed(COORDINATE_DISPLAY_FRACTION_DIGITS)}, ${lng.toFixed(COORDINATE_DISPLAY_FRACTION_DIGITS)}`;
};

/*
Generates 24 char randomized alphanumeric value. This is stored on recurring payment intent POST and
later validated for a non-password login after a user is created automatically
 */
export const generateClientToken = (): string =>
  Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);

export const promptUserToRateApp = async (): Promise<void> => {
  if (!Capacitor.isNativePlatform()) return;
  try {
    await InAppReview.requestReview();
  } catch (error) {
    Sentry.captureException(error);
  }
};

export const parsePhoneNumberString = (value: string): string => {
  return value.replace(/[^0-9]/g, '');
};

export const formatPhoneNumber = (phoneNumber: string): string => {
  const numberString = parsePhoneNumberString(phoneNumber);

  if (PhoneNumberRegex.test(numberString)) {
    return numberString.replace(
      PhoneNumberRegex,
      (_, countryCode, areaCode, telPrefix, lineNumber) => {
        return countryCode
          ? `1-${areaCode}-${telPrefix}-${lineNumber}`
          : `(${areaCode}) ${telPrefix}-${lineNumber}`;
      },
    );
  }

  if (PhoneSMSRegex.test(numberString)) {
    return numberString;
  }

  // Naive approach
  if (numberString.length > 6) {
    return `(${numberString.slice(0, 3)}) ${numberString.slice(
      3,
      6,
    )}-${numberString.slice(6)}`;
  }

  if (numberString.length > 3) {
    return `(${numberString.slice(0, 3)}) ${numberString.slice(3)}`;
  }

  return numberString;
};

export const uploadImage = async (
  imageSubmissionData: UploadImageData,
  cancelTokenSource?: CancelTokenSource,
): Promise<void> => {
  const { media, s3AuthData, s3Key } = imageSubmissionData;
  const formData = new FormData();
  formData.append('Content-Type', media.type);

  const {
    data: { url, fields },
  } = s3AuthData;

  fields.key = s3Key;

  Object.entries(fields).forEach(([k, v]) => {
    formData.append(k, v);
  });

  formData.append('file', media);

  await axios.post(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    cancelToken: cancelTokenSource?.token,
  });
};

export const verifyMediaExistsOnS3 = async (
  s3Url: string,
  s3Key: string,
): Promise<boolean> => {
  try {
    const resp = await axios.head(`${s3Url}${s3Key}`);
    return resp.status < 400;
  } catch (err) {
    return false;
  }
};

export const readFilePath = async (
  capacitorFile: Photo,
): Promise<{ file: File | Blob; type: string } | null> => {
  // Here's an example of reading a file with a full file path. Use this to
  // read binary data (base64 encoded) from plugins that return File URIs, such as
  // the Camera.
  const { path, format } = capacitorFile;
  if (!path) return null;

  const contents = await Filesystem.readFile({ path });

  const url = `data:image/${format};base64,${contents.data}`;
  const res = await fetch(url);
  const file = await res.blob();

  return { file, type: format };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getSignedLatitude = (lat: number, latRef: any): number =>
  lat && latRef === 'S' ? 0 - lat : lat;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getSignedLongitude = (lng: number, lngRef: any): number =>
  lng && lngRef === 'W' ? 0 - lng : lng;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getGPSDataFromFileIOS = (iOSFile: any): GPSData => {
  const {
    DestBearing,
    ImgDirection,
    Latitude,
    LatitudeRef,
    Longitude,
    LongitudeRef,
  } = iOSFile?.exif?.GPS || {};

  return {
    bearing: DestBearing || ImgDirection,
    lat: getSignedLatitude(Latitude, LatitudeRef),
    lng: getSignedLongitude(Longitude, LongitudeRef),
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getGPSDataFromFileAndroid = (androidFile: any): GPSData => {
  const {
    // @todo: these aren't working yet
    // GPSImgDirection,
    // GPSImgDirectionRef
    GPSLatitude,
    GPSLongitude,
  } = androidFile?.exif || {};

  return {
    bearing: null,
    lat: GPSLatitude,
    lng: GPSLongitude,
  };
};

export const formatFileUploadKey = (input: {
  fileType: string;
  s3AuthKey: string;
}): string => {
  const { fileType, s3AuthKey } = input;
  const extension = fileType === 'jpeg' ? 'jpg' : fileType;
  const now = new Date();
  const rand = Math.ceil(Math.random() * 10000);
  const datePath = formatLocalDateToUtc(now, 'M/dd');
  const fileName = `${datePath}/${now.getTime()}-${rand}.${extension}`;
  // eslint-disable-next-line no-template-curly-in-string
  return s3AuthKey.replace('${filename}', fileName);
};

export const isWildFireScheduled = (geoEvent: Wildfire): boolean => {
  return Boolean(
    geoEvent.data.isPrescribed &&
      geoEvent.data.prescribedDateStart &&
      new Date(geoEvent.data.prescribedDateStart) > new Date(),
  );
};

export const maybeTransformEmptyRichTextContentToNull = (
  content: string,
): string | null => {
  return content.length > 0 && content !== '<p></p>' ? content : null;
};

export const getEvacZoneDefaultDescription = (
  evacZones: (GeoEventEvacZoneStatus | FormEvacZoneStatus)[],
): string => {
  const displayNames = evacZones
    .map((evacZone) => evacZone.evacZone.displayName.trim())
    .sort();
  return displayNames.join(', ');
};

const isHTMLContent = (content: string): boolean => {
  return /<[a-z][\s\S]*>/i.test(content);
};

export const parseContentToHTML = (content: string): string => {
  if (isHTMLContent(content)) return content;
  const paragraphs = content.split('\n').map((paragraph) => {
    if (paragraph === '') return '';
    const pItems = paragraph.split(' ');
    const formattedPItems = pItems.map((pItem) => {
      if (pItem.startsWith('http://') || pItem.startsWith('https://')) {
        return `<a href="${pItem}">${pItem}</a>`;
      }
      return pItem;
    });
    return `<p>${formattedPItems.join(' ')}</p>`;
  });
  return paragraphs.join('');
};

export const removeHTMLTags = (html: string): string => {
  return generateText(
    generateJSON(html, RICH_TEXT_EDITOR_EXTENSIONS),
    RICH_TEXT_EDITOR_EXTENSIONS,
    {
      blockSeparator: ' ',
      textSerializers: {
        hardBreak: () => ' ',
      },
    },
  )
    .replace(/\s{2,}/g, ' ') // Replace 2 or more consecutive spaces with just 1
    .trim();
};

export const getRegionsDisplay = (regions: GeoEventRegion[]): string => {
  if (!regions.length) return '';

  if (regions.length === 1) {
    return `${regions[0].displayName}, ${regions[0].state}`;
  }

  let regionsList = regions
    .map((region) => region.displayName.replace(' County', ''))
    .join(', ');

  const replaceValue = regions.length > 2 ? ', & $1' : ' & $1';
  regionsList = regionsList.replace(/,\s([^,]+)$/, replaceValue);

  return `${regionsList} Counties`;
};

export const getAllNotificationRegions = (
  regions: NotificationRegion[],
  subscribedSettings: NotificationSetting[],
): NotificationSetting[] => {
  return regions.map((region) => ({
    region: {
      id: region.id,
      displayName: region.displayName,
      state: region.state,
      stateDisplayName: region.stateDisplayName,
      isReporterCoveredForFire: region.isReporterCoveredForFire,
    },
    setting:
      subscribedSettings.find((setting) => setting.region.id === region.id)
        ?.setting || NotificationSettingTypes.off,
  }));
};

export const getEvacZoneStylesFromRegions = (
  regions?: GeoEventRegion[],
): RegionEvacZoneStyle[] => [
  ...new Set(
    regions?.map(
      (region) => region.evacZoneStyle || EvacZoneStyles.default,
    ) || [EvacZoneStyles.default],
  ),
];

export const getEvacZoneStylesFromGeoEvent = (
  geoEvent: Wildfire | Flooding,
): RegionEvacZoneStyle[] => [
  ...new Set(
    geoEvent?.regions?.map(
      (region) => region.evacZoneStyle || EvacZoneStyles.default,
    ) || [EvacZoneStyles.default],
  ),
];

export const getEvacZoneStyleFromGeoEvent = (
  geoEvent: Wildfire | Flooding,
): RegionEvacZoneStyle => getEvacZoneStylesFromGeoEvent(geoEvent)[0];

export const findFuzzyMatch = (input: {
  query: string;
  candidates: string[];
  maxDistance?: number;
}): string | undefined => {
  const { query, candidates, maxDistance = 1 } = input;
  if (!query || !candidates.length) {
    return undefined;
  }
  const matches = [];
  for (const candidate of candidates) {
    if (candidate === query) {
      // exact match has been found, return
      return undefined;
    }
    const distance = levenshtein(query, candidate);
    if (distance <= maxDistance) {
      matches.push({ value: candidate, distance });
    }
  }
  return matches.sort((a, b) => a.distance - b.distance)[0]?.value;
};

export const getFullWordMatches = (
  text: string,
  keywords: string[],
): Array<string> => {
  if (!keywords.length) return [];

  const pronounsAllowingApostrophe = new Set([
    'i',
    'me',
    'my',
    'mine',
    'myself',
    'we',
    'us',
    'our',
    'ours',
    'ourselves',
  ]);

  const buildRegexPart = (keyword: string): string => {
    const lowerKeyword = keyword.toLowerCase();

    if (lowerKeyword === '&') {
      return '(?<!\\w)&(?!\\w)';
    }
    // Match a keyword when:
    // 1. Not preceded by a word character
    // 2. Either enclosed in quotes/parentheses or standalone
    // 3. If standalone, not followed by a letter or disallowed punctuation
    const isPronoun = pronounsAllowingApostrophe.has(lowerKeyword);

    const apostropheChars = [
      '\u0027', // Apostrophe
      '\u2019', // Right single quotation mark
      '\u02BC', // Modifier letter apostrophe
    ];
    const apostropheSuffix = `[${apostropheChars.join('')}](?:m|s|ve|re|ll)\\b`;

    const validWordSuffixes = ['.', '!', '?', ',', ';', ':', '"', '\\]', ')'];
    const wordSuffix = `[^\\s${validWordSuffixes.join('')}]`;

    const keywordPattern = isPronoun
      ? `(?:"${keyword}"|\\(${keyword}\\)|${keyword}(?:${apostropheSuffix}|(?!${wordSuffix})))`
      : `(?:"${keyword}"|\\(${keyword}\\)|${keyword}(?!${wordSuffix}))`;

    return `(?<!\\w)${keywordPattern}`;
  };

  // Filter out empty keywords and build regex
  const regexParts = keywords
    .map((keyword) => keyword.trim()) // Remove extra spaces from keywords
    .filter(Boolean)
    .map(buildRegexPart);

  if (!regexParts.length) return [];

  const regex = new RegExp(regexParts.join('|'), 'i');
  const matches = regex.exec(text);
  if (!matches) return [];
  return matches;
};

export const includesFullWord = (text: string, keywords: string[]): boolean => {
  const matches = getFullWordMatches(text, keywords);
  return matches.length > 0;
};

export const looksLikeGeneralArea = (text?: string): boolean => {
  if (!text) return true;

  const generalAreaKeywords = [
    'block',
    'and',
    '&',
    'cross',
    'at',
    'near',
    'off',
    'of',
    'basin',
  ];

  return includesFullWord(text, generalAreaKeywords);
};

export const getCurrentLocationAsShareUrl = (ts?: Date): string => {
  const pathnameWithoutPrecedingSlash = window.location.pathname.slice(1);

  // ts param helps prevent cached share previews from older versions of the entity.
  // @see https://github.com/sherwood-forestry-service/watchduty-frontend/pull/1194
  const tsParam = ts ? `?ts=${ts.getTime()}` : '';

  return `${ENV.VITE_BASE_SHARE_URL}${pathnameWithoutPrecedingSlash}${tsParam}`;
};

export const getCurrentLocationAsCameraShareUrl = (
  imageUrl: string,
): string => {
  const pathnameWithoutPrecedingSlash = window.location.pathname.slice(1);
  return `${ENV.VITE_BASE_SHARE_URL}${pathnameWithoutPrecedingSlash}?image_url=${imageUrl}`;
};

export const getRichTextFieldRequiredValidation = (
  message: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): RequiredStringSchema<string | undefined, Record<string, any>> =>
  Yup.string()
    .required(message) // Ensure it's strictly required
    .test('is-required', message, (value) => {
      const parsedValue = value ? removeHTMLTags(value).replace(/\s/g, '') : '';
      return parsedValue.length > 1;
    });

export const openNativeAppSettings = async (): Promise<void> => {
  try {
    await NativeSettings.open({
      optionAndroid: AndroidSettings.ApplicationDetails,
      optionIOS: IOSSettings.App,
    });
  } catch (error) {
    Sentry.captureException(error);
  }
};

export const openNativeAppNotificationSettings = async (): Promise<void> => {
  try {
    await NativeSettings.open({
      optionAndroid: AndroidSettings.AppNotification,
      optionIOS: IOSSettings.AppNotification,
    });
  } catch (error) {
    Sentry.captureException(error);
  }
};

export const getShallowDiff = <T extends object>(
  oldObj: T,
  newObj: T,
): {
  diff: Partial<T>;
  isEmpty: boolean;
} => {
  const diff = transform(
    newObj,
    (acc: Partial<T>, value, key: keyof T) => {
      if (!isEqual(oldObj[key], value)) {
        acc[key] = value;
      }
    },
    {},
  );
  return { diff, isEmpty: isEmpty(diff) };
};

export const isIframe = (): boolean => window.self !== window.top;

export const canUserReport = (user: User | null): boolean =>
  userInGroups(user, ['wildfire_staff_reporters', 'reporters', 'rx_reporters']);

export const canUserReportFlood = (user: User | null): boolean =>
  userInGroups(user, ['flood_staff_reporters', 'flood_reporters']);

export const canUserReportHazard = (user: User | null): boolean =>
  userInGroups(user, ['flood_staff_reporters', 'flood_reporters']);

export const isLocationEditor = (user: User | null): boolean =>
  user?.groups?.some(
    (g) =>
      g.name?.startsWith('location-') ||
      g.name?.startsWith('moderated_location-'),
  ) ?? false;

export const isInternalUser = (user: User | null): boolean =>
  userInGroups(user, ['synthetic_internal_user']);

export const canViewFlightTrackerLayer = (user: User | null): boolean =>
  user?.permissions?.canViewFlightTrackerLayer === 'always';

export const MEMBERSHIP_BENEFIT_GROUPS: UserGroups[] = [
  'synthetic_internal_user',
  'member_benefits',
  'rx_reporters',
  'reporters',
  'wildfire_staff_reporters',
  'wildfire_reporters',
  'flood_staff_reporters',
  'flood_reporters',
];

export const hasActiveMembership = (user: User | null): boolean =>
  Boolean(user?.supporterStatus?.hasActiveMembership);

export const isInMembershipBenefitGroup = (user: User | null): boolean =>
  userInGroups(user, MEMBERSHIP_BENEFIT_GROUPS);

export const hasMembershipBenefits = (user: User | null): boolean =>
  hasActiveMembership(user) || isInMembershipBenefitGroup(user);

export const isAuthorizedUser = (user: User | null): boolean =>
  userInGroups(user, ['synthetic_authorized_user']);

export const parseCoordinateFieldValue = (
  coordinate: string,
  coordType: CoordType,
): number => {
  if (!coordinate || typeof coordinate !== 'string') {
    return 0;
  }

  const trimmed = coordinate.trim();

  const patterns = {
    // Decimal degrees: 38.234512 or -122.393445 or 40 or 40.
    decimalDegrees: /^(-?\d+\.?\d*)$/,
    // Degrees decimal minutes: 38° 14.0707' N or 122° 23.6067' W
    degreesDecimalMinutes: /^\s*(\d+)\s*°\s*(\d+\.\d+)\s*[']\s*([NSEW])\s*$/i,
    // Degrees minutes seconds: 38° 14' 4.2432" N or 122° 23' 36.402" W
    degreesMinutesSeconds:
      /^\s*(\d+)\s*°\s*(\d+)\s*[']\s*(\d+\.\d+)\s*["]\s*([NSEW])\s*$/i,
  };

  const getMultiplier = (direction: string): number => {
    return /[SW]/i.test(direction) ? -1 : 1;
  };

  let result = 0;
  let match = trimmed.match(patterns.decimalDegrees);
  if (match) {
    result = parseFloat(match[1]);
  } else {
    match = trimmed.match(patterns.degreesDecimalMinutes);
    if (match) {
      const degrees = parseFloat(match[1]);
      const minutes = parseFloat(match[2]);
      const direction = match[3];

      result = getMultiplier(direction) * (degrees + minutes / 60);
    } else {
      match = trimmed.match(patterns.degreesMinutesSeconds);
      if (match) {
        const degrees = parseFloat(match[1]);
        const minutes = parseFloat(match[2]);
        const seconds = parseFloat(match[3]);
        const direction = match[4];

        result =
          getMultiplier(direction) * (degrees + minutes / 60 + seconds / 3600);
      }
    }
  }

  // Check if the value is within valid range based on coordType
  const minValue = coordType === 'latitude' ? -90 : -180;
  const maxValue = coordType === 'latitude' ? 90 : 180;

  if (result < minValue || result > maxValue) {
    return 0;
  }

  return parseCoordinate(result);
};

export const formatNameList = (t: TFunction, names: string[]): string => {
  if (names.length === 1) {
    return names[0];
  }
  if (names.length === 2) {
    return `${names[0]} ${t('pendingEvacZonesWarning.and')} ${names[1]}`;
  }
  return `${names.slice(0, -1).join(', ')}, ${t('pendingEvacZonesWarning.and')} ${names[names.length - 1]}`;
};

export const getErrorsListFromServerResponse = (
  errors: Record<string, string[]> | string[],
): string[] => {
  if (Array.isArray(errors)) {
    return errors;
  }

  if (typeof errors === 'object') {
    return Object.values(errors).flat();
  }

  return [];
};

export const getRandomFromArray = <T>(options: T[]): T =>
  options[Math.floor(Math.random() * options.length)];

export const hasValidCoordinates = <
  T extends {
    lat?: number | null;
    lng?: number | null;
  },
>(
  args: T,
): args is T & { lat: number; lng: number } => {
  const { lat, lng } = args;
  return lat != null && lng != null && isValidLat(lat) && isValidLng(lng);
};

export const isSafari = (): boolean => {
  if (typeof window === 'undefined' || !window.navigator) {
    return false;
  }

  const ua = window.navigator.userAgent;
  // Test for "Safari" and the absence of "Chrome", "CriOS", "FxiOS", etc.
  return /Safari/i.test(ua) && !/Chrome|CriOS|FxiOS|EdgiOS/i.test(ua);
};

/**
 * Safely parses a value to a number, returning undefined if invalid.
 */
export const parseToNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number') {
    return Number.isNaN(value) ? undefined : value;
  }
  if (typeof value === 'string') {
    const parsed = parseFloat(value);
    return Number.isNaN(parsed) ? undefined : parsed;
  }
  return undefined;
};

export const requestCriticalAlertsPermission = async (): Promise<void> => {
  try {
    await CriticalAlerts.requestPermission();
  } catch (error) {
    Sentry.captureException(error);
  }
};

export const getHumanReadableCapacitorPlatform = (): string => {
  const platform = Capacitor.getPlatform();

  if (platform === 'ios') {
    return 'iOS';
  }

  return capitalize(platform);
};

/**
 * Returns a UI-safe string for an object property.
 * Falls back to a placeholder when the object, property, or value is missing,
 * and supports optional value formatting.
 */
export const getDisplayValueForKey = <
  T extends Record<PropertyKey, unknown>,
  K extends keyof T,
>(
  obj: T | null | undefined,
  key: K,
  options?: {
    fallback?: string;
    format?: (value: NonNullable<T[K]>) => string;
  },
): string => {
  const value = obj?.[key];

  if (value == null || value === '') {
    return options?.fallback ?? EMPTY_DATA_VALUE;
  }

  return options?.format
    ? options.format(value as NonNullable<T[K]>)
    : String(value);
};
